import React, { useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress
} from '@mui/material'
import { FileCopy } from '@mui/icons-material'
import { useTheme } from '@mui/material/styles'
import { useDuplicateCampaignMutation } from '../../services/campaign-service'
import toast from 'react-hot-toast'

export default function DuplicateCampaignModal ({
  open,
  onClose,
  onCampaignChange,
  campaignName,
  campaignId
}) {
  const theme = useTheme()
  const [duplicateCampaign] = useDuplicateCampaignMutation()

  const [formData, setFormData] = useState({
    newName: `${campaignName} (copy)`,
    keepSchedule: true,
    includeLeads: true,
    campaignId
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [nameError, setNameError] = useState('')

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    })

    if (name === 'newName' && value.trim() === '') {
      setNameError('Campaign name is required')
    } else if (name === 'newName') {
      setNameError('')
    }
  }

  const handleSubmit = async () => {
    if (formData.newName.trim() === '') {
      setNameError('Campaign name is required')
      return
    }

    setIsSubmitting(true)
    try {
      const { message } = await duplicateCampaign(formData).unwrap()
      toast.success(message)
      handleClose()
      onCampaignChange(true)
    } catch (error) {
      setNameError(error?.data?.message || 'An error occurred while duplicating the campaign')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      newName: `${campaignName} (copy)`,
      keepSchedule: true,
      includeLeads: true,
      campaignId
    })
    setNameError('')
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 10,
          px: 1
        }
      }}
    >
      <DialogTitle>
        <Box display='flex' alignItems='center' color={theme.palette.text.primary}>
          <FileCopy sx={{ mr: 1, color: theme.palette.primary.main }} />
          Duplicate Campaign
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2, color: theme.palette.text.secondary }}>
          Create a copy of "{campaignName}" with the following settings:
        </DialogContentText>

        <TextField
          autoFocus
          margin='dense'
          id='newName'
          name='newName'
          label='New Campaign Name'
          type='text'
          fullWidth
          variant='outlined'
          value={formData.newName}
          onChange={handleChange}
          error={!!nameError}
          helperText={nameError}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={formData.keepSchedule}
              onChange={handleChange}
              name='keepSchedule'
              sx={{ color: theme.palette.primary.main }}
            />
          }
          label='Keep original schedule'
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={formData.includeLeads}
              onChange={handleChange}
              name='includeLeads'
              sx={{ color: theme.palette.primary.main }}
            />
          }
          label='Include leads from original campaign'
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant='outlined' onClick={handleClose} sx={{ color: theme.palette.primary.main }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          sx={{ backgroundColor: theme.palette.primary.main }}
          disabled={isSubmitting || formData.newName.trim() === ''}
          startIcon={isSubmitting ? <CircularProgress size={20} color='inherit' /> : null}
        >
          {isSubmitting ? 'Duplicating...' : 'Duplicate Campaign'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
